import { uploadData } from "aws-amplify/storage";
import html2canvas from "html2canvas";
// import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useState } from "react";
import { useUserStore } from "../../_store/userStore";
import api from "../../api/api.main";
import { PdfContext } from "./context/pdf.ctx";
import fontSarabunTH from "./font/fontSarabunTH";
import {
  getPdfExportProgressPercent,
  getPdfExportTotalSteps,
  PDF_CAPTURE_SCALE,
  PDF_IMAGE_QUALITY,
} from "./lib/pdf.download.helper";

/**
 * คืน control ให้ browser วาด progress ล่าสุดก่อนเริ่มงานหนักรอบถัดไป
 *
 * html2canvas และ jsPDF เป็นงาน synchronous หนักหลายช่วง จึงต้องรอหนึ่งเฟรม
 * หลัง setProgress เพื่อให้ผู้ใช้เห็นสถานะจริงระหว่างสร้างไฟล์หลายหน้า
 */
const waitForBrowserPaint = () => {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });
};

/**
 * คุมปุ่มสร้าง PDF ฝั่ง browser จาก DOM ที่ render ไว้ในหน้า /pdf
 *
 * component นี้รอให้ page DOM พร้อม, แปลงแต่ละหน้าเป็น canvas ขนาดพอดี,
 * upload ไฟล์ไป S3 และอัปเดต profile โดยใช้ progress state เดียวกันทั้ง flow
 */
function PdfDownload() {
  const userData = useUserStore((state) => state.userData);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [allPages, setAllPages] = useState(0);
  const [progress, setProgress] = useState(0);

  const pdfCtx = useContext(PdfContext);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant", // for smooth scrolling
    });
    
    const loadingTimeout = setTimeout(() => {
      setLoadingPage(false);
    }, 500);
    
    const pagesTimeout = setTimeout(() => {
      const input = document.getElementById("pdf-content");
      const pages = input?.querySelectorAll(".page");
      const pageCount = pages?.length || 0;
      setAllPages(pageCount ? getPdfExportTotalSteps(pageCount) : 0);
    }, 1000);

    // Cleanup function: always return a function
    return () => {
      clearTimeout(loadingTimeout);
      clearTimeout(pagesTimeout);
    };
  }, []);

  /**
   * สร้าง PDF ใหม่ต่อหนึ่งครั้งที่กดดาวน์โหลด
   *
   * ใช้ jsPDF instance ใหม่เสมอเพื่อไม่ให้หน้าจากรอบก่อนค้างในไฟล์,
   * ลด scale ของ html2canvas จากค่าที่หนักเกินไป และอัปเดต progress หลังจบแต่ละช่วง
   */
  const generatePDF = async () => {
    if (loadingPDF) return;

    setLoadingPDF(true);
    setProgress(0);
    try {
      pdfCtx?.setPdfDate?.(new Date());

      const input = document.getElementById("pdf-content");
      const pages = Array.from(input?.querySelectorAll(".page") || []);

      if (!pages.length) {
        setAllPages(0);
        return;
      }

      const totalSteps = getPdfExportTotalSteps(pages.length);
      const doc = new jsPDF("p", "px", "a4");

      setAllPages(totalSteps);

      doc.addFileToVFS("THSarabun.ttf", fontSarabunTH);
      doc.addFont("THSarabun.ttf", "THSarabun", "normal");
      doc.setFont("THSarabun");

      // Load font once before loop
      if (document.fonts && document.fonts.load) {
        await document.fonts.load("1em THSarabun");
      }

      for (let i = 0; i < pages.length; i++) {
        try {
          const page = pages[i];
          const isHorizontal = page.classList.contains("page-horizontal");

          // Render each page into canvas
          const canvas = await html2canvas(page, {
            scale: PDF_CAPTURE_SCALE,
            useCORS: true,
            allowTaint: false,
          });

          const image = canvas.toDataURL("image/jpeg", PDF_IMAGE_QUALITY);

          // Add page if not the first one
          if (i !== 0) {
            doc.addPage(null, isHorizontal ? "l" : "p");
          }

          // Get PDF page size
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          // Scale canvas to fit PDF page
          const widthRatio = pageWidth / canvas.width;
          const heightRatio = pageHeight / canvas.height;
          const ratio = Math.min(widthRatio, heightRatio);

          const canvasWidth = canvas.width * ratio;
          const canvasHeight = canvas.height * ratio;

          const marginX = (pageWidth - canvasWidth) / 2;
          const marginY = (pageHeight - canvasHeight) / 2;

          // Add image to PDF
          doc.addImage(
            image,
            "JPEG",
            marginX,
            marginY,
            canvasWidth,
            canvasHeight
          );

          canvas.width = 0;
          canvas.height = 0;
          setProgress(i + 1);
          await waitForBrowserPaint();
        } catch (error) {
          console.error(`❌ Error processing page ${i + 1}:`, error);
        }
      }

      // Upload and update user
      const pdfBlob = doc.output("blob");
      setProgress(pages.length + 1);
      await waitForBrowserPaint();

      await uploadData({
        key: `${userData?._id}.pdf`,
        data: pdfBlob,
      }).result;

      await api.put(`/profiles/${userData._id}`, {
        is_export: true,
        export_date: DateTime.now().setZone("Asia/Bangkok").toISO(),
      });

      setProgress(pages.length + 2);
      await waitForBrowserPaint();

      setProgress(totalSteps);
      await waitForBrowserPaint();
      doc.save("รายงานสรุป.pdf");
    } catch (error) {
      console.error("❌ Error generating PDF:", error);
    } finally {
      setLoadingPDF(false);
    }
  };

  /**
   * คืนค่าเปอร์เซ็นต์ล่าสุดจาก state ที่ trigger render จริง
   *
   * allPages เป็นจำนวนขั้นตอนรวม ไม่ใช่จำนวนหน้าอย่างเดียว เพื่อรวมช่วงสร้าง blob,
   * upload/profile update และ save ไฟล์ไว้ใน progress เดียวกัน
   */
  const getProgressPercent = () => {
    return getPdfExportProgressPercent(progress, allPages);
  };

  // console.log("allPages",allPages > 0)

  return (
    <div className="fixed top-0 right-0 z-50 ">
      {allPages > 0 && (
        <button
          className="fixed top-4 right-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-200 cursor-pointer px-6 py-3 flex items-center gap-2"
          onClick={generatePDF}
          disabled={loadingPage || loadingPDF}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {/* {loadingPDF ? `${getProgressPercent()}/100 %` : "DownloadPDF"} */}
          {loadingPDF ? (
            `${getProgressPercent()}/100 %`
          ) : (
            <div>
              <div className="text-start">DownloadPDF</div>
              <div className="text-xs text-start max-w-[187px]">
                * ดาวน์โหลด PDF ผ่าน Google Chrome บนคอมพิวเตอร์
                เพื่อไฟล์คุณภาพสูงสุด
              </div>
            </div>
          )}
        </button>
      )}
    </div>
  );
}

export default PdfDownload;
